// src/api.js
import axios from 'axios';
import { USE_REAL_API, API_BASE_URL } from './config';
import router from './router'; // 引入 Vue Router 实例，用于页面跳转

// 定义后端自定义的状态码 (与后端 HttpStatus 类对应)
const HttpStatus = {
    SUCCESS: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401, // 未授权
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    BAD_METHOD: 405,
    CONFLICT: 409,
    UNSUPPORTED_TYPE: 415,
    ERROR: 500, // 系统内部错误
    NOT_IMPLEMENTED: 501,
    WARN: 601, // 系统警告消息
    // ... 其他可能的状态码
};


// 创建 Axios 实例
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 90000, // 请求超时时间
    // Axios 默认会序列化对象为 JSON，所以 POST 请求体通常不需要额外处理
    headers: {
        'Content-Type': 'application/json' // 明确设置 Content-Type 为 JSON
    }
});

// --- 请求拦截器：在请求头中加入语言参数和 Token ---
apiClient.interceptors.request.use(
    config => {
        // 1. 添加语言参数
        const currentLanguage = localStorage.getItem('language') || 'en';
        // 注意：这里将语言代码直接作为 header 值发送 (如 'en', 'zh')
        // 如果后端需要特定的格式 (如 'ZH', 'EN')，你可能需要在这里进行转换
        config.headers['language'] = currentLanguage;

        // 2. 添加 Token 参数 (如果存在)
        const token = localStorage.getItem('token'); // 假设 token 存储在 localStorage 中
        if (token) {
            // 按照后端要求的格式设置 Token 请求头
            config.headers['Token-chat'] = `Bearer ${token}`;
        }

        // 返回修改后的配置
        return config;
    },
    error => {
        // 对请求错误做些什么
        console.error('Axios Request Error:', error);
        return Promise.reject(error);
    }
);
// --- 请求拦截器结束 ---

// --- 响应拦截器：处理后端返回的 R 对象和未授权情况 ---
apiClient.interceptors.response.use(
    response => {
        // 只处理成功响应 (HTTP 状态码 2xx)
        const res = response.data; // 后端返回的 R 对象

        // TODO: 根据后端 R 对象的 code 判断业务状态
        // 假设 SUCCESS (200) 表示业务成功
        if (res.code !== HttpStatus.SUCCESS && res.code !== HttpStatus.CREATED) {
            // 如果业务状态码不是成功，可以统一处理错误提示
            // 例如：this.$message.error(res.msg || 'Error'); // 需要在 Vue 组件中调用
            console.error('Backend Business Error:', res.msg);

            // 如果是未授权 (401)，清除登录状态并跳转到登录页
            if (res.code === HttpStatus.UNAUTHORIZED) {
                console.warn('Unauthorized access, redirecting to login...');
                localStorage.removeItem('isLoggedIn');
                localStorage.removeItem('username');
                localStorage.removeItem('token'); // 清除 token
                // 使用 router 实例进行跳转
                if (router.currentRoute.path !== '/login') {
                    router.push('/login');
                }
            }

            // 可以选择抛出错误，以便在调用接口的地方捕获并处理
            // return Promise.reject(new Error(res.msg || 'Error'));
            // 或者直接返回响应，让调用者根据 code 判断
            return response; // 返回完整的响应对象
        } else {
            // 业务状态码为成功，直接返回响应数据
            return response; // 返回完整的响应对象
        }
    },
    error => {
        // 处理非 2xx 的 HTTP 状态码错误
        console.error('Axios Response Error:', error);

        // 检查 HTTP 状态码，如果是 401 (Unauthorized)，进行跳转
        if (!error.response || (error.response && error.response.status === 401)) {
            console.warn('HTTP 401 Unauthorized, redirecting to login...');
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('username');
            localStorage.removeItem('token'); // 清除 token
            // 使用 router 实例进行跳转
            if (router.currentRoute.path !== '/login') {
                router.push('/login');
            }
        }

        // 抛出错误，以便在调用接口的地方捕获
        return Promise.reject(error);
    }
);
// --- 响应拦截器结束 ---


// --- 模拟数据和模拟函数 (当 USE_REAL_API 为 false 时使用) ---
// 注意：这里的模拟数据和逻辑与 ChatLayout.vue 中的内部模拟数据是独立的。
// 如果要完全切换到 api.js，需要修改 ChatLayout.vue 来调用这里的函数。
// 模拟数据返回结构调整为模拟后端 R 对象的结构

// 模拟网络延迟
const simulateDelay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// 模拟历史对话数据 (与 ChatLayout 中的独立，仅用于这里的 mockApi)
let mockHistory = Array.from({ length: 20 }).map((_, i) => ({
    id: i + 1,
    title: `Mock History Chat ${i + 1}` // 加上 Mock 标识区分
}));

// 模拟消息数据 (与 ChatLayout 中的独立，仅用于这里的 mockApi)
let mockMessages = {};
mockHistory.forEach(item => {
    mockMessages[item.id] = [
        { id: 1, sender: 'user', text: `Mock Hello! This is chat ${item.id}.` },
        { id: 2, sender: 'assistant', text: `Mock Hi there! How can I help you with Chat ${item.id}?` }
    ];
    if (item.id % 3 === 0) {
        mockMessages[item.id].push(
            { id: 3, sender: 'user', text: 'Mock I have another question.' },
            { id: 4, sender: 'assistant', text: 'Mock Please go ahead and ask.' }
        );
    }
});

// 模拟 API 函数
const mockApi = {
    /**
     * 模拟登录接口
     * @param {string} email - 用户邮箱 (模拟为用户名)
     * @param {string} password - 密码
     * @returns {Promise<object>} - 模拟的 R 对象结构
     */
    login(email, password) { // 参数改为 email, password
        console.log('Mock API: login called with email:', email);
        return simulateDelay(1000).then(() => {
            if (email === 'admin@example.com' && password === 'admin') { // 模拟邮箱和密码
                // 模拟成功响应的结构，符合 R 对象
                return { data: { code: HttpStatus.SUCCESS, msg: 'Mock login successful', data: { token: 'mock_token_abc123', userId: 1, username: 'MockUser' } } };
            } else {
                // 模拟失败响应的结构，符合 R 对象
                const error = new Error('Mock login failed');
                error.response = { data: { code: HttpStatus.UNAUTHORIZED, msg: 'Mock invalid email or password', data: null } };
                throw error; // 使用 throw 来模拟 Promise.reject
            }
        });
    },

    /**
     * 模拟获取聊天历史记录接口 (可能需要分页参数，这里简化)
     * @returns {Promise<object>} - 模拟的 R 对象结构，data 中包含历史记录列表
     */
    getHistory() {
        console.log('Mock API: getHistory called');
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay().then(() => {
            // 模拟返回所有历史，符合 R 对象结构
            return { data: { code: HttpStatus.SUCCESS, msg: 'Success', data: mockHistory } };
        });
    },

    /**
     * 模拟获取指定聊天消息接口
     * @param {number} chatId - 聊天会话 ID
     * @returns {Promise<object>} - 模拟的 R 对象结构，data 中包含消息列表
     */
    getMessages(chatId) {
        console.log('Mock API: getMessages called for chat:', chatId);
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay().then(() => {
            const messages = mockMessages[chatId] || [];
            // 模拟返回消息列表，符合 R 对象结构
            return { data: { code: HttpStatus.SUCCESS, msg: 'Success', data: messages } };
        });
    },

    /**
     * 模拟开始新聊天接口
     * @returns {Promise<object>} - 模拟的 R 对象结构，data 中包含新会话 ID
     */
    startNewChat() {
        console.log('Mock API: startNewChat called');
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay().then(() => {
            const maxId = Math.max(0, ...mockHistory.map(item => item.id));
            const newChatId = maxId + 1;
            const newChatItem = { id: newChatId, title: `Mock New Chat ${newChatId}` };
            // 添加到模拟历史数据 (不会持久化)
            mockHistory.unshift(newChatItem);
            mockMessages[newChatId] = [];
            // 模拟返回新对话的 ID，符合 R 对象结构
            return { data: { code: HttpStatus.CREATED, msg: 'Chat created', data: { chatId: newChatId } } };
        });
    },

    /**
     * 模拟发送消息接口
     * @param {number} chatId - 聊天会话 ID
     * @param {string} messageText - 消息文本
     * @returns {Promise<object>} - 模拟的 R 对象结构，data 中包含机器人回复消息
     */
    sendMessage(chatId, messageText) {
        console.log('Mock API: sendMessage called for chat:', chatId, 'message:', messageText);
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay(1500).then(() => { // 模拟 LLM 响应延迟
            // 将用户消息添加到模拟数据 (不会持久化)
            const messages = mockMessages[chatId] || [];
            const newUserMessageId = messages.length > 0 ? messages[messages.length - 1].id + 1 : 1;
            messages.push({ id: newUserMessageId, sender: 'user', text: messageText });
            // mockMessages[chatId] = messages; // 直接修改 messages 数组即可

            // 模拟机器人回复
            // 注意：这里的模拟回复文本是硬编码的，如果需要根据语言返回不同回复，需要更复杂的模拟逻辑
            const botReply = `Mock You said: "${messageText}". (Simulated response)`;
            const newBotMessageId = newUserMessageId + 1;
            messages.push({ id: newBotMessageId, sender: 'assistant', text: botReply });
            // mockMessages[chatId] = messages; // 直接修改 messages 数组即可

            // 模拟返回机器人消息，符合 R 对象结构
            return { data: { code: HttpStatus.CREATED, msg: 'Message sent', data: { id: newBotMessageId, sender: 'assistant', text: botReply } } };
        });
    },

    /**
     * 模拟重命名聊天接口
     * @param {number} chatId - 聊天会话 ID
     * @param {string} newTitle - 新的聊天标题
     * @returns {Promise<object>} - 模拟的 R 对象结构
     */
    renameChat(chatId, newTitle) {
        console.log('Mock API: renameChat called for chat:', chatId, 'new title:', newTitle);
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay().then(() => {
            const item = mockHistory.find(item => item.id === chatId);
            if (item) {
                item.title = newTitle;
                // 模拟成功响应，符合 R 对象结构
                return { data: { code: HttpStatus.SUCCESS, msg: 'Mock rename successful', data: null } };
            } else {
                // 模拟失败响应，符合 R 对象结构
                const error = new Error('Mock chat not found');
                error.response = { data: { code: HttpStatus.NOT_FOUND, msg: 'Mock chat not found', data: null } };
                throw error;
            }
        });
    },

    /**
     * 模拟删除聊天接口
     * @param {number} chatId - 聊天会话 ID
     * @returns {Promise<object>} - 模拟的 R 对象结构
     */
    deleteChat(chatId) {
        console.log('Mock API: deleteChat called for chat:', chatId);
        // 在模拟模式下，如果需要根据语言返回不同的模拟数据，可以在这里读取 localStorage
        // const currentLanguage = localStorage.getItem('language') || 'en';
        return simulateDelay().then(() => {
            const initialLength = mockHistory.length;
            mockHistory = mockHistory.filter(item => item.id !== chatId);
            delete mockMessages[chatId];
            if (mockHistory.length < initialLength) {
                // 模拟成功响应，符合 R 对象结构
                return { data: { code: HttpStatus.SUCCESS, msg: 'Mock delete successful', data: null } };
            } else {
                // 模拟失败响应，符合 R 对象结构
                const error = new Error('Mock chat not found');
                error.response = { data: { code: HttpStatus.NOT_FOUND, msg: 'Mock chat not found', data: null } };
                throw error;
            }
        });
    },

    // 如果后端支持分页，可以添加相应的模拟函数
    // getHistoryPage(offset, limit) { ... }
};

// --- 实际 API 函数 (当 USE_REAL_API 为 true 时使用) ---

const realApi = {
    /**
     * 实际登录接口
     * @param {string} email - 用户邮箱
     * @param {string} password - 密码
     * @returns {Promise<object>} - 后端返回的 R 对象
     * 对应的后端 Controller 方法: ChatController.login(...)
     * 接口路径: POST http://localhost:8000/chat/auth/login
     */
    login(email, password) { // 参数改为 email, password
        console.log('Real API: login called with email:', email);
        // 注意：登录接口路径是 NEW_API_BASE_URL + '/auth/login'
        // 这里直接使用 apiClient，因为它已经设置了 baseURL 为 NEW_API_BASE_URL
        return apiClient.post('/chat/auth/login', { email, password }); // 请求体为 JSON
    },

    /**
     * 实际获取聊天历史记录接口 (分页版本)
     * @param {number} [page=1] - 页码 (可选，用于分页)
     * @param {number} [size=10] - 每页数量 (可选，用于分页)
     * @returns {Promise<object>} - 后端返回的 R 对象，data 中应包含分页数据 (IPage)
     * 对应的后端 Controller 方法: ChatChatsController.getChatHistory(...)
     * 接口路径: GET http://localhost:8000/chat/chat/chats
     */
    getHistory(page = 1, size = 10) {
        console.log('Real API: getHistory called with page:', page, 'size:', size);
        // 接口路径是 NEW_API_BASE_URL + '/chat/chats'
        return apiClient.get('/chat/chats', {
            params: {
                page: page,
                size: size
            }
        });
    },

    /**
     * 实际获取指定聊天消息接口
     * @param {number} chatId - 聊天会话 ID (对应 t_chat_chats 表的 id)
     * @returns {Promise<object>} - 后端返回的 R 对象，data 中应包含消息列表
     * 对应的后端 Controller 方法: ChatMessagesController.getMessagesByChatId(...)
     * 接口路径: GET http://localhost:8000/chat/chat/messages/{chatId}
     */
    getMessages(chatId) {
        console.log('Real API: getMessages called for chat:', chatId);
        // 接口路径是 NEW_API_BASE_URL + '/chat/messages/' + chatId
        return apiClient.get(`/chat/messages/${chatId}`);
    },

    /**
     * 实际开始新聊天接口
     * @param {object} [data={}] - 可选的请求体，例如包含默认标题 { title: '...' }
     * @returns {Promise<object>} - 后端返回的 R 对象，data 中应包含新会话的 ID
     * 对应的后端 Controller 方法: ChatChatsController.createNewChat(...)
     * 接口路径: POST http://localhost:8000/chat/chat/chats
     */
    startNewChat(data = {}) { // 接收可选的请求体
        console.log('Real API: startNewChat called');
        // 接口路径是 NEW_API_BASE_URL + '/chat/chats'
        return apiClient.post('/chat/chats', data); // 请求体为 JSON
    },

    /**
     * 实际发送消息接口
     * @param {number} chatId - 聊天会话 ID (对应 t_chat_chats 表的 id)
     * @param {string} messageText - 消息文本
     * @returns {Promise<object>} - 后端返回的 R 对象，data 中应包含机器人回复消息
     * 对应的后端 Controller 方法: ChatMessagesController.sendMessage(...)
     * 接口路径: POST http://localhost:8000/chat/chat/messages
     * 请求体: { chatId: ..., content: ..., sender: 'user' }
     */
    sendMessage(chatId, messageText) {
        console.log('Real API: sendMessage called for chat:', chatId, 'message:', messageText);
        // 接口路径是 NEW_API_BASE_URL + '/chat/messages'
        return apiClient.post('/chat/messages', { // 请求体为 JSON
            chatId: chatId,
            content: messageText,
            sender: 'user' // 前端发送的消息 sender 固定为 'user'
        });
    },

    /**
     * 实际重命名聊天接口
     * @param {number} chatId - 聊天会话 ID (对应 t_chat_chats 表的 id)
     * @param {string} newTitle - 新的聊天标题
     * @returns {Promise<object>} - 后端返回的 R 对象
     * 对应的后端 Controller 方法: ChatChatsController.renameChat(...)
     * 接口路径: PUT http://localhost:8000/chat/chat/chats/{chatId}
     * 请求体: { title: ... }
     */
    renameChat(chatId, newTitle) {
        console.log('Real API: renameChat called for chat:', chatId, 'new title:', newTitle);
        // 接口路径是 NEW_API_BASE_URL + '/chat/chats/' + chatId
        return apiClient.put(`/chat/chats/${chatId}`, { // 请求体为 JSON
            title: newTitle
        });
    },

    /**
     * 实际删除聊天接口
     * @param {number} chatId - 聊天会话 ID (对应 t_chat_chats 表的 id)
     * @returns {Promise<object>} - 后端返回的 R 对象
     * 对应的后端 Controller 方法: ChatChatsController.deleteChat(...)
     * 接口路径: DELETE http://localhost:8000/chat/chat/chats/{chatId}
     */
    deleteChat(chatId) {
        console.log('Real API: deleteChat called for chat:', chatId);
        // 接口路径是 NEW_API_BASE_URL + '/chat/chats/' + chatId
        return apiClient.delete(`/chat/chats/${chatId}`);
    },

    // 如果后端支持分页，可以添加相应的函数
    // getHistoryPage(offset, limit) { return apiClient.get('/chat/chats', { params: { offset, limit } }); }
};

// 根据全局开关导出相应的 API 函数
const api = USE_REAL_API ? realApi : mockApi;

export default api;
